import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Template,
  TemplateChannel,
  TemplateFormat,
  CHANNEL_CONSTRAINTS,
  TemplateDetailDialogProps,
} from "../../types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Tabs } from "@shared/ui/tabs";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Textarea } from "@shared/ui/textarea";
import { Label } from "@shared/ui/label";
import { Badge } from "@shared/ui/badge";
import {
  Edit,
  Save,
  X,
  Copy,
  Mail,
  MessageSquare,
  BellRing,
  MessageCircle,
} from "lucide-react";
import { CharacterCounter } from "../characterCounter/CharacterCounter";
import { PlaceholderSelector } from "../placeholder/PlaceholderSelector";
import { ContentEditor } from "@shared/components/ContentEditor";
import { useToast } from "@shared/hooks/use-toast";
import { useCloneTemplate } from "../../hooks";
import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";

export const TemplateDetailDialog: React.FC<TemplateDetailDialogProps> = ({
  template,
  open,
  onClose,
  onUpdate,
  isUpdating = false,
  mode = "view",
}) => {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(mode === "edit");
  const [editedTemplate, setEditedTemplate] = useState<Template>({
    ...template,
  });
  const [newTag, setNewTag] = useState("");
  const cloneTemplateMutation = useCloneTemplate();

  // Add formState to track current form values
  const [formState, setFormState] = useState({
    templateName: template.templateName || "",
    description: template.description || "",
    channelType: template.channelType || "EMAIL",
    format: template.format || "plainText",
    category: template.templateCategory || "",
    tags: template.tags || [],
    subject: template.content?.subject || "",
    contentPlain: template.content?.body || "",
    contentRich: template.content?.body || "",
    contentHtml: template.content?.body || "",
  });

  const editForm = FormService.createForm({
    fields: [],
    mode: "onChange",
  });

  useEffect(() => {
    setEditedTemplate({ ...template });

    // Update formState when template changes
    setFormState({
      templateName: template.templateName || "",
      description: template.description || "",
      channelType: template.channelType || "EMAIL",
      format: template.format || "plainText",
      category: template.templateCategory || "",
      tags: template.tags || [],
      subject: template.content?.subject || "",
      contentPlain: template.content?.body || "",
      contentRich: template.content?.body || "",
      contentHtml: template.content?.body || "",
    });

    if (editForm) {
      editForm.setValue("templateName", template.templateName || "");
      editForm.setValue("description", template.description || "");
      editForm.setValue("channelType", template.channelType || "EMAIL");
      editForm.setValue("format", template.format || "plainText");
      editForm.setValue("category", template.templateCategory || "");
      editForm.setValue("subject", template.content?.subject || "");
      editForm.setValue("contentPlain", template.content?.body || "");
      editForm.setValue("contentRich", template.content?.body || "");
      editForm.setValue("contentHtml", template.content?.body || "");
    }
  }, [template, editForm]);

  useEffect(() => {
    setIsEditing(mode === "edit");
  }, [mode]);

  // Handle field changes to update formState
  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      setFormState((prev) => ({
        ...prev,
        [fieldName]: value,
      }));

      // Update editedTemplate tags when tags change
      if (fieldName === "tags") {
        setEditedTemplate((prev) => ({
          ...prev,
          tags: value,
        }));
      }

      // Reset format when switching from TEXT to EMAIL/WHATSAPP
      if (fieldName === "channelType" && value === "TEXT") {
        setFormState((prev) => ({
          ...prev,
          format: "plainText",
        }));
        editForm.setValue("format", "plainText");
      }
    },
    [editForm]
  );

  const handleCategoryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFieldChange("category", e.target.value);
    },
    [handleFieldChange]
  );

  const handleContentChange = useCallback(
    (fieldName: string) => (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      editForm.setValue(fieldName, value);
      handleFieldChange(fieldName, value);
    },
    [editForm, handleFieldChange]
  );

  const handlePlaceholderSelect = useCallback(
    (placeholder: string) => {
      let targetField = "contentPlain"; // default
      if (formState.format === "richText") {
        targetField = "contentRich";
      } else if (formState.format === "html") {
        targetField = "contentHtml";
      }

      const currentContent = editForm.getValues(targetField) || "";
      const newContent = currentContent + placeholder;
      editForm.setValue(targetField, newContent);
      handleFieldChange(targetField, newContent);
    },
    [formState.format, editForm, handleFieldChange]
  );

  const handleFormSubmit = async (formData: any) => {
    try {
      console.log("Form data received:", formData);
      console.log("Current formState:", formState);

      const getContentByFormat = () => {
        // Get content from formState instead of formData since custom renders don't register with form
        switch (formState.format) {
          case "plainText":
            return formState.contentPlain || "";
          case "richText":
            return formState.contentRich || "";
          case "html":
            return formState.contentHtml || "";
          default:
            return (
              formState.contentPlain ||
              formState.contentRich ||
              formState.contentHtml ||
              ""
            );
        }
      };

      const updatedTemplate: Template = {
        ...editedTemplate,
        templateName: formData.templateName,
        channelType: formData.channelType,
        format: formState.format,
        description: formData.description,
        templateCategory: formData.category,
        content: {
          body: getContentByFormat(),
          subject: formData.subject || "",
        },
        tags: editedTemplate.tags,
        lastModifiedDate: new Date().toDateString(),
        status: "published",
        isDraft: false,
      };

      console.log("Updated template:", updatedTemplate);
      setEditedTemplate(updatedTemplate);
      onUpdate(updatedTemplate);
      setIsEditing(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update template. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleFormSaveAsDraft = async (formData: any) => {
    try {
      const getContentByFormat = () => {
        // Get content from formState instead of formData
        switch (formState.format) {
          case "plainText":
            return formState.contentPlain || "";
          case "richText":
            return formState.contentRich || "";
          case "html":
            return formState.contentHtml || "";
          default:
            return (
              formState.contentPlain ||
              formState.contentRich ||
              formState.contentHtml ||
              ""
            );
        }
      };

      const updatedTemplate: Template = {
        ...editedTemplate,
        templateName: formData.templateName,
        channelType: formData.channelType,
        format: formState.format,
        description: formData.description,
        templateCategory: formData.category,
        content: {
          body: getContentByFormat(),
          subject: formData.subject || "",
        },
        tags: editedTemplate.tags,
        lastModifiedDate: new Date().toDateString(),
        status: "draft",
        isDraft: true,
      };

      setEditedTemplate(updatedTemplate);
      onUpdate(updatedTemplate);
      setIsEditing(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save template as draft. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCloneTemplate = async (templateId: number) => {
    try {
      await cloneTemplateMutation.mutateAsync(templateId);
    } catch (error) {}
  };

  const handleAddTag = () => {
    if (newTag.trim() && !editedTemplate.tags.includes(newTag.trim())) {
      const updatedTags = [...editedTemplate.tags, newTag.trim()];
      setEditedTemplate({
        ...editedTemplate,
        tags: updatedTags,
      });
      handleFieldChange("tags", updatedTags);
      setNewTag("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    const updatedTags = editedTemplate.tags.filter(
      (tag) => tag !== tagToRemove
    );
    setEditedTemplate({
      ...editedTemplate,
      tags: updatedTags,
    });
    handleFieldChange("tags", updatedTags);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  const getChannelIcon = (channel: TemplateChannel) => {
    switch (channel) {
      case "EMAIL":
        return <Mail className="h-4 w-4" />;
      case "TEXT":
        return <MessageSquare className="h-4 w-4" />;
      case "NOTIFICATION":
        return <BellRing className="h-4 w-4" />;
      case "WHATSAPP":
        return <MessageCircle className="h-4 w-4" />;
      default:
        return null;
    }
  };

  // Calculate character count based on current format
  const getCurrentContent = (formState: any) => {
    switch (formState.format) {
      case "plainText":
        return formState.contentPlain;
      case "richText":
        return formState.contentRich;
      case "html":
        return formState.contentHtml;
      default:
        return (
          formState.contentPlain ||
          formState.contentRich ||
          formState.contentHtml ||
          ""
        );
    }
  };

  const characterCount = getCurrentContent(formState).length;
  const maxCharacters =
    CHANNEL_CONSTRAINTS[formState.channelType]?.maxCharacters;
  const isOverCharacterLimit = maxCharacters
    ? characterCount > maxCharacters
    : false;

  const canSave =
    !isOverCharacterLimit &&
    formState.templateName.trim() &&
    getCurrentContent(formState).trim() &&
    (formState.channelType !== "EMAIL" || formState.subject?.trim());

  const shouldShowTabs = mode === "view" && !isEditing;
  const shouldShowDirectEdit =
    mode === "edit" || (mode === "view" && isEditing);

  const editFormFields = useMemo(
    (): FormField[] => [
      {
        name: "templateName",
        type: "text",
        label: "Template Name",
        required: true,
        placeholder: "e.g., Payment Confirmation",
        maxLength: 100,
        defaultValue: formState.templateName,
      },
      {
        name: "description",
        type: "textarea",
        label: "Description",
        placeholder: "Brief description of this template's purpose",
        rows: 3,
        maxLength: 500,
        defaultValue: formState.description,
      },
      {
        name: "channelType",
        type: "radio",
        label: "Communication Channel",
        required: true,
        options: [
          { value: "EMAIL", label: "Email" },
          { value: "TEXT", label: "SMS" },
          { value: "WHATSAPP", label: "WhatsApp" },
        ],
        defaultValue: formState.channelType,
      },
      {
        name: "format",
        type: "radio",
        label: "Template Format",
        required: true,
        options: [
          { value: "plainText", label: "PlainText" },
          { value: "richText", label: "RichText" },
          { value: "html", label: "Html" },
        ],
        defaultValue: formState.format,
        showWhen: () => {
          const channel = formState.channelType;
          return channel === "EMAIL";
        },
      },
      {
        name: "category",
        type: "text",
        label: "",
        required: true,
        defaultValue: formState.category,
        customRender: (field, form) => (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Category <span className="text-red-500">*</span>
            </label>
            <Input
              {...form.register("category")}
              onChange={handleCategoryChange}
              defaultValue={formState.category}
              className="w-full"
              disabled={isUpdating}
            />
          </div>
        ),
      },
      {
        name: "tags",
        type: "multi-select",
        label: "",
        defaultValue: formState.tags,
        customRender: (field, form) => (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Tags
            </label>

            <div className="flex flex-wrap gap-2">
              {editedTemplate.tags.map((tag) => (
                <Badge
                  key={tag}
                  variant="secondary"
                  className="flex items-center gap-1"
                >
                  {tag}
                  {!isUpdating && (
                    <X
                      className="h-3 w-3 cursor-pointer"
                      onClick={() => handleRemoveTag(tag)}
                    />
                  )}
                </Badge>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <Input
                placeholder="Add tag"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault(); // Prevent form submission
                    handleAddTag();
                  }
                }}
                className="flex-1"
                disabled={isUpdating}
              />
              <Button
                type="button" // Add this to prevent form submission!
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.preventDefault(); // Additional prevention
                  handleAddTag();
                }}
                disabled={isUpdating}
              >
                Add
              </Button>
            </div>
          </div>
        ),
      },
      {
        name: "subject",
        type: "text",
        label: "Subject",
        placeholder: "Enter template subject",
        defaultValue: formState.subject,
        required: formState.channelType === "EMAIL",
        showWhen: () => formState.channelType === "EMAIL",
      },
      {
        name: "placeholderSelector",
        type: "select",
        label: "",
        customRender: (field, form) => (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Insert Placeholder
            </label>

            <PlaceholderSelector
              onSelectPlaceholder={handlePlaceholderSelect}
            />
          </div>
        ),
        showWhen: () => formState.contentRich !== undefined,
      },
      {
        name: "contentPlain",
        type: "textarea",
        label: "Content (Plain Text)",
        required: formState.format === "plainText",
        placeholder: "Enter your plain text content here...",
        rows: 10,
        defaultValue: formState.contentPlain,
        showWhen: () => formState.format === "plainText",
        helperText: "Plain text format - no formatting options available.",
        customRender: (field, form) => (
          <div className="space-y-2">
            <textarea
              {...form.register("contentPlain")} // Register with form
              placeholder="Enter your plain text content here..."
              rows={10}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
              onChange={(e) => {
                handleContentChange("contentPlain")(e);
                form.setValue("contentPlain", e.target.value); // Sync with form
              }}
              value={formState.contentPlain || ""}
            />
            <p className="text-xs text-gray-500">
              Plain text format - no formatting options available.
            </p>
          </div>
        ),
      },
      {
        name: "contentRich",
        type: "rich-text",
        label: "Content (Rich Text)",
        required: formState.format === "richText",
        placeholder: "Enter your rich text content here...",
        defaultValue: formState.contentRich,
        showWhen: () => formState.format === "richText",
        helperText: "Rich text with formatting toolbar available.",
        customRender: (field, form) => (
          <div className="space-y-2">
            <textarea
              {...form.register("contentRich")} // Register with form
              placeholder="Enter your rich text content here..."
              rows={10}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
              onChange={(e) => {
                handleContentChange("contentRich")(e);
                form.setValue("contentRich", e.target.value); // Sync with form
              }}
              value={formState.contentRich || ""}
            />
            <p className="text-xs text-gray-500">
              Rich text with formatting toolbar available.
            </p>
          </div>
        ),
      },
      {
        name: "contentHtml",
        type: "html-text",
        label: "Content (HTML)",
        required: formState.format === "html",
        placeholder: "Enter your HTML content here...",
        defaultValue: formState.contentHtml,
        showWhen: () => formState.format === "html",
        helperText: "Raw HTML editing mode for advanced users.",
        customRender: (field, form) => (
          <div className="space-y-2">
            <textarea
              {...form.register("contentHtml")} // Register with form
              placeholder="Enter your HTML content here..."
              rows={10}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
              onChange={(e) => {
                handleContentChange("contentHtml")(e);
                form.setValue("contentHtml", e.target.value); // Sync with form
              }}
              value={formState.contentHtml || ""}
            />
            <p className="text-xs text-gray-500">
              Raw HTML editing mode for advanced users.
            </p>
          </div>
        ),
      },
    ],
    [
      formState,
      isUpdating,
      editedTemplate.tags,
      handleCategoryChange,
      handleContentChange,
      handlePlaceholderSelect,
      handleRemoveTag,
      handleAddTag,
      handleKeyDown,
      newTag,
    ]
  );

  const viewModeTabComponents = useMemo(
    () => ({
      preview: {
        label: "Preview",
        component: (
          <div className="mt-4">
            {editedTemplate.channelType === "EMAIL" &&
              editedTemplate.content.subject && (
                <div className="mb-4">
                  <Label>Subject:</Label>
                  <div className="bg-muted p-2 rounded-md font-medium">
                    {editedTemplate.content.subject}
                  </div>
                </div>
              )}
            <div className="bg-muted p-4 rounded-md whitespace-pre-wrap">
              {editedTemplate.content.body}
            </div>
            <div className="mt-2">
              <CharacterCounter
                currentCount={editedTemplate.content.body.length}
                maxCount={
                  CHANNEL_CONSTRAINTS[editedTemplate.channelType]?.maxCharacters
                }
              />
            </div>
            <div className="mb-4 space-y-3">
              {editedTemplate.templateCategory && (
                <div>
                  <Label className="text-sm font-medium text-gray-700">
                    Category:
                  </Label>
                  <div className="bg-muted p-2 rounded-md font-medium">
                    {editedTemplate.templateCategory}
                  </div>
                </div>
              )}

              {editedTemplate.tags && editedTemplate.tags.length > 0 && (
                <div>
                  <Label className="text-sm font-medium text-gray-700">
                    Tags:
                  </Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {editedTemplate.tags.map((tag) => (
                      <Badge key={tag} variant="secondary">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ),
      },
    }),
    [editedTemplate]
  );

  const DirectEditForm = useMemo(() => {
    return (
      <div className="space-y-4 mt-4">
        <DynamicForm
          fields={editFormFields}
          form={editForm}
          onSubmit={handleFormSubmit}
          onFieldChange={handleFieldChange}
          config={{
            layout: "vertical",
            spacing: "comfortable",
            showSubmitButton: false,
            showResetButton: false,
            classes: {
              form: "space-y-4",
              input:
                "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
              textarea:
                "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
              select:
                "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
              label: "block text-sm font-medium text-gray-700 mb-1",
              error: "text-sm text-red-600 mt-1",
              container: "space-y-1",
            },
          }}
        />

        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleFormSaveAsDraft(editForm.getValues())}
            disabled={isUpdating}
            className="flex items-center gap-2"
          >
            {isUpdating ? (
              <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
            ) : null}
            {isUpdating ? "Saving..." : "Save as Draft"}
          </Button>

          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (mode === "edit") {
                  onClose();
                } else {
                  setIsEditing(false);
                }
              }}
              disabled={isUpdating}
            >
              Cancel
            </Button>

            <Button
              type="button"
              onClick={() => editForm.handleSubmit(handleFormSubmit)()}
              disabled={!canSave || isUpdating}
              className="flex items-center gap-2"
            >
              {isUpdating ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {isUpdating
                ? "Updating..."
                : template.isDraft == false
                ? "Update"
                : "Update"}
            </Button>
          </div>
        </div>
      </div>
    );
  }, [
    editFormFields,
    editForm,
    handleFormSubmit,
    handleFieldChange,
    isUpdating,
    canSave,
    mode,
    onClose,
    template.isDraft,
  ]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {getChannelIcon(template.channelType)}
              <Badge variant="outline" className="capitalize">
                {template.channelType}
              </Badge>
              {template.isDefault && <Badge variant="secondary">Default</Badge>}
              <Badge
                variant={template.isDraft === true ? "outline" : "secondary"}
              >
                {template.isDraft === true ? "Draft" : "Published"}
              </Badge>
            </div>
          </div>

          {!shouldShowDirectEdit && (
            <>
              <DialogTitle className="mt-2">
                <span>{template.templateName}</span>
              </DialogTitle>
              <DialogDescription>
                <p>{template.description}</p>
              </DialogDescription>
            </>
          )}
        </DialogHeader>

        {shouldShowDirectEdit ? (
          DirectEditForm
        ) : shouldShowTabs ? (
          <Tabs
            className="mt-4"
            defaultValue="preview"
            tabsListClassName="grid grid-cols-1"
            tabComponents={viewModeTabComponents}
            disableUrlSync={true}
          />
        ) : null}

        <div className="mt-4 text-sm text-muted-foreground">
          <p>Created: {template.createdDate}</p>
          <p>Last updated: {template.lastModifiedDate}</p>
        </div>

        <DialogFooter className="gap-2 sm:gap-0 flex-wrap">
          {shouldShowDirectEdit ? null : (
            <>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => handleCloneTemplate(template?.id)}
                  disabled={cloneTemplateMutation.isPending}
                  className="flex gap-2"
                >
                  {cloneTemplateMutation.isPending ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  {cloneTemplateMutation.isPending ? "Cloning" : "Clone"}
                </Button>
                {!template.isDefault && template.isDraft == true && (
                  <Button
                    onClick={() => setIsEditing(true)}
                    className="flex gap-2"
                    disabled={isUpdating}
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
